import type { MouseEvent, ReactNode } from 'react';
import { CopyIdButton } from '../../../components/common/CopyIdButton/CopyIdButton';
import { formatDate, formatTime } from '../../../shared/utils/dateFormat';

/**
 * Shared cell content for DataTable columns — the handful of shapes every
 * Dashboard V2 table used to copy-paste.
 */

/** Status pill with a colored dot (sessions, transactions, stations, users). */
export const StatusBadgeCell = ({
  badgeClass,
  dotClass,
  label,
}: {
  badgeClass: string;
  dotClass?: string;
  label: ReactNode;
}) => (
  <span className={`status-badge ${badgeClass}`}>
    {dotClass && <span className={`status-dot ${dotClass}`} aria-hidden="true" />}
    {label}
  </span>
);

/** Stacked date + time (the `session-date-cell` idiom). */
export const DateTimeCell = ({
  value,
  center,
}: {
  value: string;
  center?: boolean;
}) => (
  <div className="session-date-cell" style={center ? { alignItems: 'center' } : undefined}>
    <span className="session-date">{formatDate(value)}</span>
    <span className="session-time">{formatTime(value)}</span>
  </div>
);

/** Truncated id/email with a copy button; renders a dash when empty. */
export const IdWithCopyCell = ({
  value,
  copyLabel,
  emptyText = '-',
}: {
  value: string | null | undefined;
  copyLabel: string;
  emptyText?: string;
}) =>
  value ? (
    <span className="cell-id-with-copy">
      <span className="cell-id-text" title={value}>
        {value}
      </span>
      <CopyIdButton value={value} label={copyLabel} variant="icon" />
    </span>
  ) : (
    <>{emptyText}</>
  );

/** Icon button for a row action (edit/delete/view/qr). */
export const TableActionButton = ({
  icon,
  onClick,
  ariaLabel,
  className = 'table-action-btn',
  disabled,
  title,
  stopPropagation,
}: {
  icon: string;
  onClick: () => void;
  ariaLabel: string;
  className?: string;
  disabled?: boolean;
  title?: string;
  /** For buttons inside clickable rows. */
  stopPropagation?: boolean;
}) => (
  <button
    type="button"
    className={className}
    onClick={(e: MouseEvent) => {
      if (stopPropagation) e.stopPropagation();
      onClick();
    }}
    disabled={disabled}
    title={title}
    aria-label={ariaLabel}
  >
    <span className="material-symbols-outlined" aria-hidden="true">
      {icon}
    </span>
  </button>
);
